/**
 * @caint/data — catálogo do instrumento v4.2 (300 itens) + catálogos de apoio.
 *
 * Seed autoritativo, derivado do protótipo (`seed.json`). É a fonte de verdade
 * para popular o banco no backend e para o estado inicial do PWA offline.
 * Validado em QA: 300 itens, soma por setor = 300 (ver `catalogo.test.ts`).
 */

import type { ItemAvaliado } from "@caint/engine";
import type {
  Cargo,
  CategoriaContextual,
  Ciclo,
  DimensaoIMS,
  Divergencia,
  ImgrForm,
  ItemCatalogo,
  ItemPercepcao,
  MacroCodigo,
  MatrizPermissoes,
  MonitoramentoSeed,
  Papel,
  Perspectiva,
  Pessoa,
  RefRisco,
  RiscoContextual,
  Setor,
  SupAmostra,
  SupChecklistItem,
  SupContestacao,
  SupItemFase,
  Unidade,
  Versao,
} from "@caint/shared";
// Cargos da unidade (cargo→nível→área→perfil), gerado da Matriz Adélia Matos.
import cargos from "./cargos.json" with { type: "json" };
// Questionário IMGR (31 questões em 6 componentes, escala 0–3) — Manual IMGR.
import imgrForm from "./imgr.json" with { type: "json" };
import seed from "./seed.json" with { type: "json" };
// Cadastro oficial da rede EMSERH (104 unidades). Gerado de docs/lista_unidades.md
// por `scripts/gen-unidades.mjs` — id = número oficial da lista (string).
import unidades from "./unidades.json" with { type: "json" };

/* INSTRUMENTO / REFERÊNCIA — permanecem (não são demo). */
export const SETORES = seed.SECTORS as readonly Setor[];
export const MACROS = seed.MACRO as Readonly<Record<MacroCodigo, string>>;
export const UNIDADES = unidades as readonly Unidade[];
export const CARGOS = cargos as readonly Cargo[];
export const IMGR_FORM = imgrForm as ImgrForm;
export {
  calcularImgr,
  calcularDashboardImgr,
  faixaImgr,
  type ResultadoImgr,
  type ComponenteImgr,
  type DashboardImgr,
  type ContagemRotulo,
  type ComponentePerfilImgr,
  type QuestaoResumoImgr,
} from "./imgr.ts";
export {
  parsearMatriz,
  perfilDoNivel,
  competenciaMatriz,
  mesesDesde,
  modeloMatriz,
  statusMatriz,
  MESES_VALIDADE_MATRIZ,
  NOME_MODELO_MATRIZ,
  type AbaModeloMatriz,
  type ResultadoMatriz,
  type StatusMatriz,
} from "./matriz.ts";
export {
  RELATORIO_INSTITUCIONAL,
  type RelatorioInstitucional,
  type SignatarioRelatorio,
} from "./relatorioInstitucional.ts";
export const RISCOS_REFERENCIAIS = seed.REF_RISCOS as readonly RefRisco[];
export const PERMISSOES = seed.PERMISSOES as MatrizPermissoes;
export const VERSOES = seed.VERSOES as readonly Versao[];
export const DIMENSOES_IMS = seed.IMS_DIMS as readonly DimensaoIMS[];
export const ITENS_PERCEPCAO = seed.PERCEP_ITENS as readonly ItemPercepcao[];
export const PERSPECTIVAS = seed.PERSPECTIVAS as readonly Perspectiva[];
export const SUP_CHECKLIST = seed.SUP_CHECKLIST as readonly SupChecklistItem[];
export const SUP_PRE = seed.SUP_PRE as readonly SupItemFase[];
export const SUP_CAMPO = seed.SUP_CAMPO as readonly SupItemFase[];
export const SUP_VALIDACAO = seed.SUP_VALID as readonly SupItemFase[];
export const CICLO = seed.CYCLE as Ciclo;
export const CATEGORIAS_CONTEXTUAIS: readonly CategoriaContextual[] = (
  seed.CTX_CATEGORIAS as readonly string[]
).map((nome, indice) => ({ indice, nome }));

/** Catálogo dos 300 itens SEM as respostas-semente (mi/mf zeradas). */
export const CATALOGO: readonly ItemCatalogo[] = (seed.CATALOG as ItemCatalogo[]).map((it) => ({
  ...it,
  mi: null,
  mf: null,
}));

/**
 * Nº de itens do catálogo por setor (05A..05R), derivado da FONTE autoritativa
 * (os próprios itens, não o metadado `Setor.itens` do seed). Alimenta a tela de
 * escopo ("Y itens no escopo") e o `tamanhoAmostraEfetivo` (doc 17b §3.5).
 */
export const ITENS_POR_SETOR: Readonly<Record<string, number>> = Object.freeze(
  CATALOGO.reduce<Record<string, number>>((acc, it) => {
    acc[it.sector] = (acc[it.sector] ?? 0) + 1;
    return acc;
  }, {}),
);

/** Papéis de acesso + `presidente` (abre/encerra) + `visualizador` (só leitura). */
export const PAPEIS: readonly Papel[] = [
  ...(seed.ROLES as Papel[]),
  { key: "presidente", nome: "Presidente CAINT", eixo: "—", desc: "Abre e encerra ciclos de avaliação" },
  {
    key: "visualizador",
    nome: "Visualizador (só leitura)",
    eixo: "—",
    desc: "Acompanha todo o sistema, sem editar",
  },
];

/* INSTÂNCIAS DEMO REMOVIDAS — começam vazias; preenchidas no ciclo real. */
export const MONITORAMENTO_SEED: MonitoramentoSeed = {
  ago: { c1: 0, c2: 0, c3: 0 },
  set: { c1: 0, c2: 0, c3: 0 },
  out: { c1: 0, c2: 0, c3: 0 },
  ims: [0, 0, 0, 0, 0, 0],
};
export const PESSOAS: readonly Pessoa[] = [];
export const DIVERGENCIAS: readonly Divergencia[] = [];
export const CONTEXTUAIS_SEED: readonly RiscoContextual[] = [];
export const SUP_AMOSTRA: readonly SupAmostra[] = [];
export const SUP_CONTESTACOES: readonly SupContestacao[] = [];

/** Catálogo no formato que o motor consome (subconjunto de campos). */
export function catalogoAvaliavel(): readonly ItemAvaliado[] {
  return CATALOGO.map((it) => ({
    peso: it.peso,
    eixo: it.eixo,
    p: it.p,
    i: it.i,
    mi: it.mi ?? null,
    mf: it.mf ?? null,
  }));
}
